import type { MembershipRole } from "@safeplate/contracts";

export type NavIcon =
  | "attention"
  | "history"
  | "log"
  | "more"
  | "records"
  | "reports"
  | "settings"
  | "setup"
  | "tasks"
  | "team"
  | "today";

export interface NavItem {
  badge?: "attention";
  icon: NavIcon;
  label: string;
  to: string;
}

export interface NavGroup {
  items: readonly NavItem[];
  label: string | null;
}

export interface PhoneBarItem {
  badge?: "attention";
  icon: NavIcon;
  kind: "link" | "log" | "more";
  label: string;
  to?: string;
}

export interface SettingsSection {
  adminOnly: boolean;
  icon:
    | "business"
    | "computer"
    | "data"
    | "notifications"
    | "account"
    | "plan"
    | "privacy";
  key: string;
  label: string;
  to: string;
}

const today: NavItem = { icon: "today", label: "Today", to: "/" };
const newLog: NavItem = { icon: "log", label: "New log", to: "/log" };
const history: NavItem = { icon: "history", label: "History", to: "/records" };
const attention: NavItem = {
  badge: "attention",
  icon: "attention",
  label: "Attention",
  to: "/attention",
};
const records: NavItem = { icon: "records", label: "Records", to: "/records" };
const tasks: NavItem = { icon: "tasks", label: "Tasks", to: "/tasks" };
const team: NavItem = { icon: "team", label: "Team", to: "/team" };
const setup: NavItem = { icon: "setup", label: "Setup", to: "/setup" };
const reports: NavItem = { icon: "reports", label: "Reports", to: "/reports" };
const settings: NavItem = {
  icon: "settings",
  label: "Settings",
  to: "/settings",
};

export const newLogItem: NavItem = newLog;

export function canManage(role: MembershipRole): boolean {
  return role !== "staff";
}

export function sidebarGroups(role: MembershipRole): readonly NavGroup[] {
  if (!canManage(role)) {
    return [{ items: [today, newLog, history], label: null }];
  }
  return [
    { items: [today, attention, records, tasks], label: "Daily" },
    { items: [team, setup, reports], label: "Manage" },
  ];
}

export function phoneBar(role: MembershipRole): readonly PhoneBarItem[] {
  if (!canManage(role)) {
    return [
      { icon: "today", kind: "link", label: "Today", to: "/" },
      { icon: "log", kind: "link", label: "New log", to: "/log" },
      { icon: "history", kind: "link", label: "History", to: "/records" },
    ];
  }
  return [
    { icon: "today", kind: "link", label: "Today", to: "/" },
    {
      badge: "attention",
      icon: "attention",
      kind: "link",
      label: "Attention",
      to: "/attention",
    },
    { icon: "log", kind: "log", label: "Log", to: "/log" },
    { icon: "records", kind: "link", label: "Records", to: "/records" },
    { icon: "more", kind: "more", label: "More" },
  ];
}

export function moreItems(role: MembershipRole): readonly NavItem[] {
  return canManage(role) ? [tasks, team, setup, reports, settings] : [];
}

const sections: readonly SettingsSection[] = [
  {
    adminOnly: false,
    icon: "notifications",
    key: "notifications",
    label: "My notifications",
    to: "/settings/notifications",
  },
  {
    adminOnly: false,
    icon: "computer",
    key: "computer",
    label: "This computer",
    to: "/settings/computer",
  },
  {
    adminOnly: false,
    icon: "account",
    key: "account",
    label: "Account",
    to: "/settings/account",
  },
  {
    adminOnly: true,
    icon: "business",
    key: "business",
    label: "Business details",
    to: "/settings/business",
  },
  {
    adminOnly: true,
    icon: "plan",
    key: "plan",
    label: "Plan & billing",
    to: "/settings/plan",
  },
  {
    adminOnly: true,
    icon: "data",
    key: "data",
    label: "Backups",
    to: "/settings/data",
  },
  {
    adminOnly: true,
    icon: "privacy",
    key: "privacy",
    label: "Privacy requests",
    to: "/settings/privacy",
  },
];

// Founder decision, 24 Oct 2026: Help opens the support page in a new tab
// and shows the support email. Visible to every role; not a route.
export const settingsHelp = {
  address: "hello@safeplate.nz",
  href: "https://safeplate.nz/support",
  label: "Help",
} as const;

export function settingsSections(
  role: MembershipRole,
): readonly SettingsSection[] {
  return sections.filter((section) => !section.adminOnly || role === "admin");
}

export interface UserMenuModel {
  addPerson: boolean;
  signOutLabel: string;
  switchPerson: boolean;
}

export function userMenu(
  role: MembershipRole,
  peopleCount: number,
): UserMenuModel {
  const shared = peopleCount >= 2;
  return {
    addPerson: true,
    signOutLabel: shared ? "Sign out of this computer" : "Sign out",
    switchPerson: shared,
  };
}

export function canSignOutEveryone(
  role: MembershipRole,
  peopleCount: number,
): boolean {
  return peopleCount >= 2 && canManage(role);
}

const staffPaths = [
  "/",
  "/log",
  "/records",
  "/tasks",
  "/inbox",
  "/settings",
  "/settings/notifications",
  "/settings/computer",
  "/settings/account",
] as const;

const adminOnlyPaths = [
  "/settings/business",
  "/settings/plan",
  "/settings/data",
  "/settings/privacy",
] as const;

function normalise(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

function under(path: string, candidate: string): boolean {
  return path === candidate || path.startsWith(`${candidate}/`);
}

export function canAccess(role: MembershipRole, pathname: string): boolean {
  const path = normalise(pathname);
  if (role === "admin") return true;
  if (adminOnlyPaths.some((candidate) => under(path, candidate))) return false;
  if (role === "manager") return true;
  return (staffPaths as readonly string[]).includes(path);
}

export function accessRedirect(
  role: MembershipRole,
  pathname: string,
): "/" | "/settings" | null {
  if (canAccess(role, pathname)) return null;
  return role === "staff" ? "/" : "/settings";
}

export function countLabel(count: number): string | null {
  if (!Number.isFinite(count) || count <= 0) return null;
  return count > 99 ? "99+" : String(Math.floor(count));
}
